import React, { useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useDepartments } from "@/contexts/DepartmentsContext";
import { Save, X, Loader2 } from "lucide-react";

/**
 * QuickViewMachinery — flat editable table for the Machinery register.
 *
 * Machinery keeps a richer schema than Assets (service intervals, hours,
 * tech links), but only a handful of fields need touching during a
 * walk-around: name, category, department, location, manufacturer, model,
 * serial, status. Everything else stays in MachineFormDialog for full edits.
 */
const STATUSES = [
  { v: "available", l: "Available" },
  { v: "in_use", l: "In use" },
  { v: "needs_service_unconfirmed", l: "Service due?" },
  { v: "needs_service", l: "Needs service" },
  { v: "broken_unconfirmed", l: "Broken?" },
  { v: "broken", l: "Broken" },
  { v: "retired", l: "Retired" },
];

const QuickViewMachinery = ({ items, categories, onSaved }) => {
  const { departments } = useDepartments();
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  const catNames = useMemo(
    () => (categories || []).map((c) => c.name).filter(Boolean),
    [categories],
  );

  const patch = useCallback((id, field, value) => {
    setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [field]: value } }));
  }, []);

  const cancel = (id) =>
    setDrafts((d) => {
      const n = { ...d };
      delete n[id];
      return n;
    });

  const save = async (item) => {
    const body = { ...(drafts[item.id] || {}) };
    if (Object.keys(body).length === 0) return;
    setSavingId(item.id);
    try {
      await api.put(`/machines/${item.id}`, body);
      toast.success(`Saved "${item.name}"`);
      cancel(item.id);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSavingId(null);
    }
  };

  const val = (item, field) =>
    drafts[item.id]?.[field] !== undefined
      ? drafts[item.id][field]
      : item[field] ?? "";

  if (!items?.length) {
    return (
      <div className="nb-card p-10 text-center text-sm text-[#607087]" data-testid="quickview-machinery-empty">
        No machines match your filters.
      </div>
    );
  }

  return (
    <div className="nb-card p-0 overflow-x-auto" data-testid="quickview-machinery-table">
      <table className="min-w-[1200px] w-full text-sm">
        <thead className="bg-[#F4F8FB] border-b-2 border-black">
          <tr className="text-left">
            <th className="p-3 label-eyebrow w-[18%]">Machine</th>
            <th className="p-3 label-eyebrow w-[11%]">Category</th>
            <th className="p-3 label-eyebrow w-[10%]">Dept</th>
            <th className="p-3 label-eyebrow w-[13%]">Location</th>
            <th className="p-3 label-eyebrow w-[11%]">Manufacturer</th>
            <th className="p-3 label-eyebrow w-[11%]">Model</th>
            <th className="p-3 label-eyebrow w-[10%]">Serial</th>
            <th className="p-3 label-eyebrow w-[9%]">Status</th>
            <th className="p-3 label-eyebrow w-[7%] text-right sticky right-0 bg-[#F4F8FB]">
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((m) => {
            const dirty = !!drafts[m.id] && Object.keys(drafts[m.id]).length > 0;
            const busy = savingId === m.id;
            return (
              <tr
                key={m.id}
                data-testid={`quickview-machine-row-${m.id}`}
                className={`border-b border-black/10 ${dirty ? "bg-[#FFF7DB]" : ""}`}
              >
                <td className="p-3 align-top">
                  <input
                    data-testid={`quickview-machine-name-${m.id}`}
                    className="qv-input font-semibold"
                    value={val(m, "name") ?? ""}
                    onChange={(e) => patch(m.id, "name", e.target.value)}
                  />
                  <div className="text-[10px] text-[#607087] mt-0.5">
                    {m.service_interval_days
                      ? `every ${m.service_interval_days}d`
                      : "no service cycle"}
                  </div>
                </td>
                <td className="p-2 align-top">
                  <select
                    data-testid={`quickview-machine-category-${m.id}`}
                    className="qv-input"
                    value={val(m, "category") || ""}
                    onChange={(e) => patch(m.id, "category", e.target.value)}
                  >
                    <option value="">— pick —</option>
                    {val(m, "category") && !catNames.includes(val(m, "category")) && (
                      <option value={val(m, "category")}>
                        {val(m, "category")} (legacy)
                      </option>
                    )}
                    {catNames.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </td>
                <td className="p-2 align-top">
                  <select
                    data-testid={`quickview-machine-dept-${m.id}`}
                    className="qv-input"
                    value={val(m, "department") || ""}
                    onChange={(e) => patch(m.id, "department", e.target.value)}
                  >
                    <option value="">— none —</option>
                    {(departments || []).map((d) => (
                      <option key={d.id} value={d.name}>{d.name}</option>
                    ))}
                  </select>
                </td>
                <td className="p-2 align-top">
                  <input
                    data-testid={`quickview-machine-location-${m.id}`}
                    className="qv-input"
                    value={val(m, "location") || ""}
                    onChange={(e) => patch(m.id, "location", e.target.value)}
                    placeholder="e.g. Workshop area A"
                  />
                </td>
                <td className="p-2 align-top">
                  <input
                    data-testid={`quickview-machine-manufacturer-${m.id}`}
                    className="qv-input"
                    value={val(m, "manufacturer") || ""}
                    onChange={(e) => patch(m.id, "manufacturer", e.target.value)}
                  />
                </td>
                <td className="p-2 align-top">
                  <input
                    data-testid={`quickview-machine-model-${m.id}`}
                    className="qv-input"
                    value={val(m, "model") || ""}
                    onChange={(e) => patch(m.id, "model", e.target.value)}
                  />
                </td>
                <td className="p-2 align-top">
                  <input
                    data-testid={`quickview-machine-serial-${m.id}`}
                    className="qv-input font-mono-num"
                    value={val(m, "serial") || ""}
                    onChange={(e) => patch(m.id, "serial", e.target.value)}
                  />
                </td>
                <td className="p-2 align-top">
                  <select
                    data-testid={`quickview-machine-status-${m.id}`}
                    className="qv-input"
                    value={val(m, "status") || "available"}
                    onChange={(e) => patch(m.id, "status", e.target.value)}
                  >
                    {STATUSES.map((s) => (
                      <option key={s.v} value={s.v}>{s.l}</option>
                    ))}
                  </select>
                </td>
                <td className="p-2 align-top text-right sticky right-0 bg-white">
                  {dirty ? (
                    <div className="inline-flex gap-1">
                      <button
                        data-testid={`quickview-machine-save-${m.id}`}
                        onClick={() => save(m)}
                        disabled={busy}
                        className="nb-btn nb-btn-primary px-2 py-1.5 text-xs flex items-center gap-1"
                      >
                        {busy ? (
                          <Loader2 size={12} className="animate-spin" strokeWidth={2.5} />
                        ) : (
                          <Save size={12} strokeWidth={2.5} />
                        )}
                        Save
                      </button>
                      <button
                        data-testid={`quickview-machine-cancel-${m.id}`}
                        onClick={() => cancel(m.id)}
                        disabled={busy}
                        className="nb-btn bg-white p-1.5"
                      >
                        <X size={12} strokeWidth={2.5} />
                      </button>
                    </div>
                  ) : (
                    <span className="text-[10px] text-[#607087] italic">saved</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default QuickViewMachinery;
